const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

// Products that may only be added once to the cart (digital goods / programs).
// Add a product by handle, or by variant id when only a specific variant
// (e.g. a digital version sold alongside a physical one) should be limited.
// Power of Posture is a single product (handle: power-of-posture-softcover) with
// two variants — Electronic eBook (digital) and Physical Copy. Only the digital
// one is limited / bundled, so it is targeted by variant id, not product handle.
const POP_EBOOK_VARIANT_ID = 39646905106593;

const SINGLE_QTY_HANDLES = [
  'the-10-week-online-program',
  'human-optimization-bundle-1',
  'the-functional-training-system-1',
  'rg-bar-workout',
];
const SINGLE_QTY_VARIANTS = [
  POP_EBOOK_VARIANT_ID, // Power of Posture — Electronic eBook (digital)
];

// True for a cart line item (from /cart.js or /cart.json) limited to qty 1.
function isSingleQtyCartItem(item) {
  return SINGLE_QTY_HANDLES.includes(item.handle) || SINGLE_QTY_VARIANTS.includes(item.variant_id);
}

// Identity used to dedupe a single-qty good across the whole cart. Handle-based
// limits collapse every variant of the product to one unit; variant-based limits
// (e.g. the Power of Posture eBook) collapse only that variant.
function singleQtyIdentity(item) {
  return SINGLE_QTY_HANDLES.includes(item.handle) ? 'handle:' + item.handle : 'variant:' + item.variant_id;
}

// Reconcile mutually-exclusive / single-qty products in the cart.
//   - The Human Optimization Bundle replaces the 10-week program, FTS and the
//     Power of Posture eBook.
//   - A single-qty product (e.g. the digital RG Bar Workout) may exist only once
//     in the whole cart: across ALL line items its total quantity is capped at 1.
//     This holds no matter how the item was added — standalone, via a bundle, or
//     as a duplicate line — while leaving non-limited goods (e.g. the RG Bar) free
//     to be bought in any quantity.
// Returns the updated cart (with the requested `sections` rendered) or null when
// nothing needed changing, so callers can render the original add response as-is.
function reconcileCartExclusives(sections, sectionsUrl) {
  return fetch('/cart.json')
    .then((res) => res.json())
    .then((data) => {
      const bundleExist = data.items.some((item) => item.handle === 'human-optimization-bundle-1');
      const keptSingleQty = new Set();
      let changed = false;

      // Positional quantities — one entry per line item, in cart order.
      const quantities = data.items.map((item) => {
        let qty = item.quantity;

        // The bundle replaces its constituent products
        if (
          bundleExist &&
          (item.handle === 'the-10-week-online-program' ||
            item.variant_id == POP_EBOOK_VARIANT_ID ||
            item.handle === 'the-functional-training-system-1')
        ) {
          qty = 0;
        }

        // Single-qty products: only one unit may exist across the whole cart.
        if (qty > 0 && isSingleQtyCartItem(item)) {
          const identity = singleQtyIdentity(item);
          if (keptSingleQty.has(identity)) {
            qty = 0; // a unit is already kept on an earlier line — drop this one
          } else {
            keptSingleQty.add(identity);
            if (qty > 1) qty = 1; // cap the kept line to a single unit
          }
        }

        if (qty !== item.quantity) changed = true;
        return qty;
      });

      // Nothing to reconcile — let the caller render its original response
      if (!changed) return null;

      const body = { updates: quantities };
      if (sections) body.sections = sections;
      if (sectionsUrl) body.sections_url = sectionsUrl;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      }).then((res) => res.json());
    });
}
